<template>
  <button :title="title" :aria-label="title" :class="$style.button" @click.prevent.enter="onClick">
    <SvgIcon :svg="HeartFill" :class="[$style.icon, isLiked && $style.isLiked]" />
  </button>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { likeItem, unlikeItem } from '@/js/lib/api/item';
import { useItemStore } from '@/js/lib/store/item';
import { useUserStore } from '@/js/lib/store/user';
import { loading } from '@/js/lib/ui';
import { signinSignup } from '@/js/lib/ui/signin-signup';
import SvgIcon from '@/js/components/molecules/SvgIcon.vue';
import HeartFill from '@/icon/vue/heart-fill.svg';

const props = defineProps<{
  itemId: number;
}>();
const userStore = useUserStore();
const itemStore = useItemStore();
const isLoading = ref(false);
const isLiked = computed(() => itemStore.likedItemIds.includes(props.itemId));
const title = computed(() => (isLiked.value ? 'お気に入りから外す' : 'お気に入りに追加'));
const like = async () => {
  itemStore.addLikedItemId(props.itemId);
  await likeItem({ itemId: props.itemId }).catch(() => {
    itemStore.removeLikedItemId(props.itemId);
  });

  window.dataLayer?.push({
    event: 'like',
    event_category: 'kpi',
    event_label: 'item',
  });
};
const unlike = async () => {
  itemStore.removeLikedItemId(props.itemId);
  await unlikeItem({ itemId: props.itemId }).catch(() => {
    itemStore.addLikedItemId(props.itemId);
  });
};
const onClick = async () => {
  if (userStore.isAuthenticated) {
    if (isLoading.value) {
      return;
    }

    isLoading.value = true;
    await (isLiked.value ? unlike() : like());
    isLoading.value = false;
  } else {
    signinSignup({
      next: window.location.href,
      callback: async () => {
        await loading({
          asyncFunc: like,
          text: 'ログインしています',
        });
        window.location.reload();
      },
    });
  }
};
</script>

<style module lang="scss">
.button {
  display: block;
}

.icon {
  cursor: pointer;
  display: block;
  fill: var(--color-black-alpha30);
  padding: 2px 5px;
  stroke: var(--color-white);
  stroke-width: 1.6;
  width: 38px;

  @media (--sp) {
    -webkit-tap-highlight-color: var(--color-black-alpha0);
  }

  &:hover {
    fill: var(--color-black-alpha40);
  }

  &.isLiked {
    fill: $action-button-heart-color;
  }
}
</style>
